import { CommonModule } from '@angular/common';
import { Component, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { GoogleMap, GoogleMapsModule } from '@angular/google-maps';
import { MapThemeDirective } from 'src/app/directives/map-theme.directive';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleChange, MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatToolbarModule } from '@angular/material/toolbar';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { addMinutes, format } from 'date-fns';
import { jsPDF } from 'jspdf';
import { domToCanvas } from 'modern-screenshot';
import { NoDataComponent } from 'projects/ui-library/src/lib/components/no-data/no-data.component';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { GalleryTool } from 'src/app/classes/gallery/gallery.tools';
import { Pop } from 'src/app/classes/shared/pop';
import { normalizeModel } from 'src/app/classes/shared/tools';
import { ActionDialogComponent } from 'src/app/components/project-pages/actions/dialogs/action-dialog/action-dialog.component';
import { RiskReportComponent } from 'src/app/components/project-pages/risks/risk-report/risk-report.component';
import { ALL_ROLES, GalleryComponent, TagDisplayConfig } from 'src/app/components/shared/gallery/gallery.component';
import { Action } from 'src/app/interfaces/api/action';
import { Attachment } from 'src/app/interfaces/api/attachment';
import { PutMetric, Risk } from 'src/app/interfaces/api/metric';
import { defaultModel, Model } from 'src/app/interfaces/api/model';
import { defaultProject, Project } from 'src/app/interfaces/api/project';
import { defaultUser, User, ViewerPosition } from 'src/app/interfaces/api/user';
import { Config } from 'src/app/interfaces/shared/config';
import { Tag } from 'src/app/interfaces/shared/tag';
import { TimeFormatPipe } from 'src/app/pipes/time-format.pipe';
import { ActionService } from 'src/app/services/api/actions/action.service';
import { AttachmentService } from 'src/app/services/api/attachments/attachment.service';
import { MetricService } from 'src/app/services/api/metrics/metric.service';
import { ModelService } from 'src/app/services/api/models/model.service';
import { ProjectService } from 'src/app/services/api/projects/project.service';
import { UnitService } from 'src/app/services/api/units/unit.service';
import { UserService } from 'src/app/services/api/users/user.service';
import { AuthService } from 'src/app/services/core/auth.service';
import { ButtonComponent, ChipComponent, SpinnerComponent } from 'ui-library';

@Component({
  selector: 'app-risk',
  standalone: true,
  imports: [
    ButtonComponent,
    MatButtonModule,
    MatToolbarModule,
    MatIconModule,
    TranslateModule,
    CommonModule,
    MatCheckboxModule,
    FormsModule,
    GoogleMapsModule,
    MapThemeDirective,
    MatDividerModule,
    MatFormFieldModule,
    MatDatepickerModule,
    MatInputModule,
    MatSelectModule,
    MatNativeDateModule,
    MatMenuModule,
    MatSlideToggleModule,
    TimeFormatPipe,
    GalleryComponent,
    ChipComponent,
    MatCardModule,
    SpinnerComponent,
    NoDataComponent
  ],
  templateUrl: './risk.component.html',
  styleUrls: ['./risk.component.scss']
})

export class RiskComponent implements OnInit, OnDestroy {
  @ViewChild(GoogleMap) map!: GoogleMap;
  @ViewChild('maps', { static: false }) maps!: any;

  project: Project = defaultProject;
  projectId: string | null;
  user: User = defaultUser;
  modelId: string = "";
  colors = Config.colors;
  mapOptions: google.maps.MapOptions = {};
  destroy$ = new Subject<void>();
  viewerPositions: ViewerPosition[] = [];
  mapObject: any = {};
  showMap = false;
  projectXY = [0, 0];
  risk!: Risk | null;
  loading: boolean = true;
  riskId: string;

  model: Model = defaultModel;
  imageAngle: number = 0;
  mapWidth = 600;
  mapHeight = 600;
  groundOverlay: any;
  markers: Record<string, google.maps.Marker> = {};
  userLastPosition: ViewerPosition[] = [];
  icons: any;
  mapLoaded: boolean = false;
  attachments: Record<string, Attachment> = {}
  helpText: string = "Les actions suggérées ici sont remplies par un expert chaque jour. Elles sont susceptibles d'être améliorées par vos retours terrains.";
  loadingPDF: boolean = false;
  noData: boolean = false;
  linkedActions: { id: string; title: string }[] = [];
  tagDisplayConfig: TagDisplayConfig = {
    visibility: {
      risk: { ai: ['dev'], manual: ALL_ROLES },
    }
  };

  mapOnly = false;

  constructor(
    private auth: AuthService,
    private metricService: MetricService,
    private userService: UserService,
    private projectService: ProjectService,
    private unitService: UnitService,
    private pop: Pop,
    private route: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
    private attachmentService: AttachmentService,
    private translate: TranslateService,
    private modelService: ModelService,
    private galleryTool: GalleryTool,
    private actionService: ActionService,
  ) {
    this.riskId = this.route.snapshot.params['id'];

    if (!this.riskId)
      this.riskId = this.route.snapshot.queryParamMap.get('id') || '';

    this.projectId = this.route.snapshot.queryParamMap.get('project');
    const key = this.route.snapshot.queryParamMap.get('key');
    this.mapOnly = this.route.snapshot.queryParamMap.get('mapOnly') == 'true' ? true : false;

    if (this.projectId && key) {
      this.auth.setProjectKey(this.projectId, key);
      this.projectService.getProjectById(this.projectId).pipe(takeUntil(this.destroy$)).subscribe((project: any) => {
        this.project = project;
        this.modelId = this.project.model as string;
        this.getRisk();
      });
    } else if (this.projectId) {
      this.projectService.getProjectById(this.projectId).pipe(takeUntil(this.destroy$)).subscribe((project: any) => {
        this.project = project;

        this.projectService.getStatus(project.database).pipe(takeUntil(this.destroy$)).subscribe(result => {
          this.project.status = result[0];
          this.projectService.setCurrentProject(project);
        });

        this.projectService.setCurrentProject(project);

        this.userService.setCurrentProject(this.projectId!).pipe(takeUntil(this.destroy$)).subscribe(async (user: any) => {
          this.user = user;
          this.viewerPositions = this.user.viewerPositions;

          if (!this.modelId || this.modelId != this.project.model) {
            this.modelId = this.project.model as string;
          }

          await this.getRisk();
        });
      });
    } else {
      this.projectService.currentProject.pipe(takeUntil(this.destroy$)).subscribe(async res => {
        if (!res.database) return;

        this.unitService.getUnits().pipe(take(1)).subscribe(async (units: any[]) => {
          if (this.project && this.project.id !== res.id) {
            this.project = res;
            this.ngOnInit();
          } else {
            this.project = res;
          }

          if (!this.modelId || this.modelId != this.project.model) {
            this.modelId = this.project.model as string;
          }

          await this.getRisk();
        });
      });

      this.user = this.auth.currentUser();
      this.viewerPositions = this.user?.viewerPositions;
    }
  }

  ngOnInit(): void {
    this.risk = null;
    this.loading = true;
    this.mapLoaded = false;
    this.attachments = {};
    this.markers = {};
  }

  @HostListener('window: resize', ['$event'])
  onWindowResize(event: Event) {
    this.calculateMapSize();
  }

  async getRisk() {
    this.loading = true;
    this.risk = null;

    this.metricService.getMetric(this.riskId).pipe(takeUntil(this.destroy$)).subscribe(async risk => {
      if (risk) {
        this.risk = risk;
        let { lat, lon, zone, sequence } = this.risk.payload;
        this.risk.payload["displayTitle"] = this.galleryTool.getDisplayName(this.risk.payload["title"]!);

        if (lat && lon) {
          await this.initializeMap();
        }

        if (["Non défini", "Not defined"].includes(zone!))
          this.risk.payload.zone = "";

        if (["Non défini", "Not defined"].includes(sequence!))
          this.risk.payload.sequence = "";

        this.risk.gallery = await this.getAttachment(this.risk!.payload.attachment as string)
        this.loadLinkedActions();
        this.loading = false;
      } else {
        this.loading = false;
        this.noData = true;
      }
    }, err => {
      this.loading = false;
      this.noData = true;
    });
  }

  async initializeMap() {
    if (!this.modelId) return;

    this.showMap = true;
    await this.loadIcons();
    this.getModel();
  }

  calculateMapSize() {
    if (this.maps?.nativeElement) {
      const { width, height } = this.maps.nativeElement.getBoundingClientRect();
      this.mapHeight = height;
      this.mapWidth = width;
    }
  }

  getModel() {
    this.modelService.getModel(this.modelId).pipe(takeUntil(this.destroy$)).subscribe(async results => {
      try {
        this.model = results;
        if (this.risk?.payload?.lat && this.risk?.payload?.lon) {
          const { lat, lon } = this.risk.payload;
          results.mapCenter = { lat, lon };
        }
        const normalized = normalizeModel(results);
        let object = {
          mapCenter: normalized.mapCenter,
          mapCoord: normalized.mapCoord,
          origin: normalized.origin,
          mapImage: normalized.mapImage ?? (normalized as any).mapImageSrc ?? ""
        };

        this.userLastPosition = this.viewerPositions.filter(t => t.projectId == this.project.id);
        this.mapObject = object;
        await new Promise(res => setTimeout(res, 400));
        this.loadMap(this.mapObject);
        await this.setMarker();
      } catch (e) {
        console.error("Error: ", e);
      }
    }, error => {
      this.showMap = false;
      console.error(error);
    });
  }

  loadMap(object: any) {
    this.mapLoaded = false;
    const lat = object.mapCenter?.lat;
    const lng = object.mapCenter?.lon;
    let mapOptions: google.maps.MapOptions = {
      center: { lat, lng },
      zoom: this.mapOptions?.zoom || (this.userLastPosition.length != 0 ? this.userLastPosition[0]?.zoom : 18),
      tilt: this.userLastPosition.length != 0 ? this.userLastPosition[0]?.tilt : 0,
      mapTypeId: google.maps.MapTypeId.SATELLITE,
      rotateControl: false
    }
    this.mapOptions = mapOptions;

    if (this.model.origin.mapAngle > 0) {
      this.rotationImage(this.model.origin.mapAngle);
    } else {
      this.updateMap(object);
    }

    this.map.mapDblclick.pipe(takeUntil(this.destroy$)).subscribe((data) => {
      const streetView = this.map.getStreetView();
      streetView.setOptions({
        position: { lat: data.latLng!.lat(), lng: data.latLng!.lng() },
        pov: { heading: 70, pitch: -10 },
      });
      streetView.setVisible(true);
    });

    this.map.googleMap!.setOptions({ zoomControl: object.origin.zoomEnabled, gestureHandling: object.origin.panEnabled ? 'greedy' : 'none' });
  }

  async updateMap(object: any) {
    if (this.groundOverlay) {
      this.groundOverlay.setMap(null);
    }

    const imageUrl = object.mapImage ?? object.mapImageSrc ?? null;
    this.groundOverlay = new google.maps.GroundOverlay(
      imageUrl,
      object.mapCoord,
      { clickable: false }
    );
    this.groundOverlay.setMap(this.map.googleMap);

  }

  async setMarker() {
    if (!this.risk || !this.risk.payload.lat || !this.risk.payload.lon) return;

    let color = this.colors.danger;
    this.risk.color = color;

    let icon: string;
    switch (this.risk.payload.title) {
      case "Chute de hauteur":
        icon = "hauteur";
        break;
      case "Levage Elingage":
        icon = "levage";
        break;
      case "Coactivité":
      case "Co-activité Engins Piétons":
        icon = "coactivite";
        break;
      case "Gestes et postures":
        icon = "posture";
        break;
      case "Chute de plain-pied":
        icon = "plain-pied";
        break;
      case "Basculement Renversement Effondrement":
        icon = "effondrement";
        break;
      case "Ensevelissement":
        icon = "ensevelissement";
        break;
      default:
        icon = "danger";
        break;
    }

    if (!this.icons[icon]) {
      return;
    }

    let size = 50;
    var regSize = new RegExp('{{ size }}', 'g');
    var regScale = new RegExp('{{ scale }}', 'g');

    let svg = this.icons[icon].replaceAll('{{ color }}', this.colors.danger).replace('{{ iconColor }}', '#FFF');
    svg = svg.replace(regSize, size);
    svg = svg.replace(regScale, 1);

    this.risk.icon = await this.loadImageAsPNG('data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg), 40, 40);

    let marker = new google.maps.Marker({
      position: new google.maps.LatLng(this.risk.payload.lat, this.risk.payload.lon),
      map: this.map.googleMap,
      title: this.risk.payload.title,
      zIndex: 9,
      opacity: 1,
      visible: true,
      icon: this.risk.icon
    });

    this.markers[this.risk.id] = marker;
    this.mapLoaded = true;
  }

  loadIcons() {
    return new Promise((resolve, reject) => {
      try {
        var icons = ['danger', 'hauteur', 'plain-pied', 'effondrement', 'levage', 'coactivite', 'ensevelissement', 'posture'];

        var loadCount = 0;
        if (!this.icons) {
          this.icons = {};
          icons.forEach((icon) => {
            let xhr, url;
            url = `/models/risks/SVG/${icon}.svg`;

            xhr = new XMLHttpRequest();
            xhr.open('GET', url, true);
            xhr.onload = async () => {
              this.icons[icon] = xhr.responseText;
              loadCount++;
              if (loadCount == icons.length) resolve(true);
            }
            xhr.send(null);
          });
        } else {
          resolve(true);
        }
      } catch (e) {
        console.error(e);
      }
    });
  }

  loadImageAsPNG(url: string, height: number, width: number): Promise<string> {
    return new Promise((resolve, reject) => {
      let sourceImage = new Image();

      sourceImage.onload = () => {
        let png = new Image();
        let cnv = document.createElement('canvas');
        cnv.height = height;
        cnv.width = width;

        let ctx = cnv.getContext('2d');

        ctx!.drawImage(sourceImage, 0, 0, height, width);
        png.src = cnv.toDataURL();
        resolve(png.src);
      }

      sourceImage.src = url;
    });
  }

  onCommentEdit(event: Event) {
    this.metricService.setMetric(this.risk as PutMetric).subscribe(res => {
    });
  }

  onShowImage() {
    try {
      let risk = this.risk;
      if (!risk) return;
      if (risk.payload?.attachment) {
        this.openGallery(risk.payload.attachment);
      } else {
        this.attachmentService.getAttachments({ createdDate: risk.createdDate }).subscribe(attachments => {
          if (attachments && attachments.length) {
            if (!this.risk!.payload.attachment)
              this.risk!.payload.attachment = attachments[0]!.id;
            this.openGallery(attachments[0]!.id);
          } else {
            this.pop.error(this.translate.instant("risks.image_not_found"));
          }
        });
      }
    } catch (err: any) {
      console.error("Error while loading gallery :>>", err.toString());
    }
  }

  createAction(risk: Risk | null) {
    if (!risk) return;
    const riskTitle = risk.payload['displayTitle'] || risk.payload.title;
    const ref = this.dialog.open(ActionDialogComponent, {
      width: '720px',
      data: {
        mode: 'add',
        action: {
          source: 'risk',
          risks: [risk.id],
          riskTitles: riskTitle ? [riskTitle] : [],
          title: this.translate.instant('actions.from_risk_prefix') + ' ' + (riskTitle || ''),
          description: risk.payload.description || '',
        },
        projectId: this.project?.id,
      },
    });
    ref.afterClosed().subscribe(res => {
      if (res) this.loadLinkedActions();
    });
  }

  private loadLinkedActions() {
    if (!this.riskId) return;
    const query: any = {};
    if (this.project?.id) query.project = this.project.id;
    this.actionService.getActions(query).pipe(takeUntil(this.destroy$)).subscribe({
      next: (actions: Action[]) => {
        this.linkedActions = (actions || [])
          .filter(a => this.actionLinkedToRisk(a))
          .map(a => ({ id: a.id, title: a.title }));
      },
      error: () => { this.linkedActions = []; },
    });
  }

  openAction(actionId: string) {
    this.actionService.getAction(actionId).pipe(take(1)).subscribe(action => {
      if (!action) return;
      const ref = this.dialog.open(ActionDialogComponent, {
        width: '720px',
        data: { mode: 'edit', action: { ...action }, projectId: this.project?.id },
      });
      ref.afterClosed().subscribe(res => { if (res) this.loadLinkedActions(); });
    });
  }

  private actionLinkedToRisk(action: Action): boolean {
    const ids = (action.risks || []).map((r: any) =>
      r && typeof r === 'object' && 'id' in r ? r.id : (r as string)
    );
    if (ids.includes(this.riskId)) return true;
    // Legacy single-risk field support
    const legacy = (action as any).risk;
    const legacyId = legacy && typeof legacy === 'object' && 'id' in legacy ? legacy.id : legacy;
    return legacyId === this.riskId;
  }

  reportRisk() {
    const dialogRef = this.dialog.open(RiskReportComponent, {
      width: "600px",
      data: {
        risk: this.risk
      }
    });

    dialogRef.afterClosed().subscribe((res) => {
      if (res.ok && res.comment) {
        this.risk!.payload.comment = res.comment;
        this.metricService.reportRisk(this.risk!).subscribe(result => {
          this.pop.toast(this.translate.instant("risks.report_save"), "success", 4000, "save");
        });
      }
    });
  }

  async openGallery(attachmentId: string) {
    const getImageInfo = () => {
      return new Promise((resolve, reject) => {
        if (this.attachments[attachmentId]) {
          resolve(this.getImageInfoData(this.attachments[attachmentId]));
        } else {
          this.attachmentService.getAttachment(attachmentId).subscribe(attachment => {
            if (attachment) {
              this.attachments[attachmentId] = attachment;
              resolve(this.getImageInfoData(attachment));
            } else {
              this.pop.toast(this.translate.instant("risks.image_not_found"), "danger");
              reject("no image found");
            }
          });
        }
      });
    }

    const dialogRef = this.dialog.open(GalleryComponent, {
      width: "auto",
      height: "auto",
      data: await getImageInfo()
    });

    dialogRef.afterClosed().subscribe((res) => {
    });
  }

  getImageInfoData(attach: Attachment) {
    return {
      index: 0,
      id: attach._id || attach.id,
      name: attach.name,
      tracker: attach.tracker,
      project: this.project,
      urls: [attach.url],
      tags: attach.tags,
      startDate: attach.createdDate,
      endDate: addMinutes(attach.createdDate, 1).toISOString(),
      processed: attach.processed,
      user: this.user,
      customVisionIds: attach.customVisionIds ?? {},
      type: "Attachments",
      boxes: attach.predictions,
      display: ["risk"],
      tagDisplayConfig: this.tagDisplayConfig
    };
  }

  rotationImage(angleDegrees: number) {
    const canvasContainer = document.createElement('canvas-container');
    const canvas = document.createElement('canvas');

    const ctx: any = canvas.getContext('2d');

    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => {
      let width = image.width;
      let height = image.height;

      const angle = angleDegrees * Math.PI / 180;

      const rotatedWidth = Math.abs(width * Math.cos(angle)) + Math.abs(height * Math.sin(angle));
      const rotatedHeight = Math.abs(width * Math.sin(angle)) + Math.abs(height * Math.cos(angle));

      canvas.width = rotatedWidth;
      canvas.height = rotatedHeight;

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      ctx.translate(rotatedWidth / 2, rotatedHeight / 2);
      ctx.rotate(angle);

      ctx.drawImage(image, -width / 2, -height / 2, width, height);

      canvasContainer.style.width = rotatedWidth + 'px';
      canvasContainer.style.height = rotatedHeight + 'px';

      this.model.mapImage = canvas.toDataURL();
      this.model.origin.mapAngle = angleDegrees;

      this.imageAngle = angleDegrees;

      this.updateMap(this.model);
    }

    image.onerror = (error) => {
      console.log('Load image error :>> ', error);
    }

    image.src = this.model.mapImageSrc as string;
  }

  goBack() {
    this.router.navigate(['/risks']);
  }

  async getCanvas() {
    try {
      return await domToCanvas(this.maps.nativeElement);
    } catch (e) {
      console.error(e);
      // Create a fallback blank canvas
      const fallbackCanvas = document.createElement('canvas');
      const ctx = fallbackCanvas.getContext('2d');
      fallbackCanvas.width = 300;
      fallbackCanvas.height = 150;

      if (ctx) {
        ctx.fillStyle = '#f8d7da';
        ctx.fillRect(0, 0, fallbackCanvas.width, fallbackCanvas.height);
        ctx.fillStyle = '#721c24';
        ctx.font = '16px sans-serif';
        ctx.fillText('Error: Unable to render canvas', 10, 75);
      }
      return fallbackCanvas;
    }
  }

  async exportPDF() {
    this.loadingPDF = true;
    await new Promise(res => setTimeout(res, 300));
    this.calculateMapSize();
    const pdf = new jsPDF('l', 'px', 'a4');
    let textLeft = 500;
    let titleY = 16;
    let mSize = 290;
    let mapSize = 130;
    let margin = 10;
    let width = pdf.internal.pageSize.getWidth();
    let height = pdf.internal.pageSize.getHeight();

    try {
      // Add background
      pdf.addImage("/img/export-bg.jpg", "JPEG", 0, 0, width, height);

      // Get attachment/image
      let att: any = this.risk!.gallery || await this.getAttachment(this.risk!.payload.attachment as string);
      const imgProps = pdf.getImageProperties(att.url);
      const imgRatio = imgProps.width / imgProps.height;
      let imgWidth = (mSize - 20) * imgRatio;
      let imgHeight = mSize - 20;

      // Add main image
      pdf.addImage(imgProps.data, "JPEG", margin, margin, imgWidth, imgHeight);

      // Draw bounding box if risk tag exists
      let risk = att.tags?.risk?.find((tag: Tag) => tag.probability == 1);
      if (risk) {
        let { left, top, width: boxWidth, height: boxHeight } = risk.boundingBox;
        let x = Math.floor(margin + (left * imgWidth));
        let y = Math.floor(margin + (top * imgHeight));
        let riskWidth = Math.floor(boxWidth * imgWidth);
        let riskHeight = Math.floor(boxHeight * imgHeight);
        pdf.setDrawColor("#ff0000");
        pdf.setLineWidth(0.5);
        pdf.rect(x, y, riskWidth, riskHeight);
        pdf.setDrawColor("#000000");
        pdf.setLineWidth(0.2);
      }

      // Add map if lat/lon exists
      let mapWidth = this.showMap && this.risk!.payload.lat && this.risk!.payload.lon ? mapSize * this.mapWidth / this.mapHeight : 0;
      let notesMargin = mapWidth ? 10 : 0;

      if (mapWidth) {
        let canvas = await this.getCanvas();
        if (canvas) {
          let png = canvas.toDataURL('png');
          pdf.addImage(png, "png", margin, mSize, mapWidth, mapSize);
        }
      }

      // Notes box
      pdf.rect(mapWidth ? mapWidth + 20 : margin, mSize, (mSize - 20) * imgRatio - mapWidth - notesMargin, mapSize);
      pdf.setTextColor("#000000");
      pdf.setFontSize(12);
      pdf.text(`Notes`, mapWidth + 15 + notesMargin, mSize + 10, { maxWidth: 80 });

      let y = titleY + 10;

      // Title
      pdf.setTextColor("#000000");
      pdf.setFontSize(12);
      pdf.text(`${this.translate.instant('risks.risk_report')}`, textLeft, y, { maxWidth: 80 });

      y += 12;
      // Date
      let date = `${format(this.risk!.payload.startDate as string, "dd/MM/yy HH:mm")}`;
      pdf.setTextColor("#666666");
      pdf.setFontSize(7);
      pdf.text(date, textLeft, y, { maxWidth: 80 });

      y += 12;
      // Project title
      pdf.setTextColor("#666666");
      pdf.setFontSize(9);
      pdf.text(`${this.translate.instant('projects.project')}`, textLeft, y, { maxWidth: 80 });

      y += 12;
      // Project name
      pdf.setTextColor("#000000");
      pdf.setFontSize(10);
      pdf.text(this.project.name, textLeft, y, { maxWidth: 80 });

      y += (this.project.name.length > 20 ? 20 : 14);

      // Resource title
      pdf.setTextColor("#666666");
      pdf.setFontSize(9);
      pdf.text(`${this.translate.instant('units.unit')}`, textLeft, y, { maxWidth: 80 });

      y += 12;
      // Unit name
      pdf.setTextColor("#000000");
      pdf.setFontSize(10);
      pdf.text(this.risk!.unitName || 'N/A', textLeft, y, { maxWidth: 80 });

      y += (this.risk!.unitName && this.risk!.unitName.length > 20 ? 20 : 14);

      // Detected risk title
      pdf.setTextColor("#666666");
      pdf.setFontSize(9);
      pdf.text(`${this.translate.instant('risks.detected_risk')}`, textLeft, y, { maxWidth: 80 });

      y += 12;
      // Risk title
      pdf.setTextColor("#000000");
      pdf.setFontSize(11);
      let title = this.risk!.payload["displayTitle"] || this.risk?.payload["title"];
      pdf.text(title, textLeft, y, { maxWidth: 80 });

      y += (title.length > 20 ? 20 : 12);
      // Details
      const text = `${format(this.risk!.createdDate, 'yyyy-MM-dd HH:mm')}\r\n\r\nZone: ${this.risk!.payload.zone || 'N/A'}\r\n\r\nSequence: ${this.risk!.payload.sequence || 'N/A'}
        \r\n\r\n${this.risk!.payload.description || ''}\r\n\r\n${this.risk!.payload.comment ? 'Comment: ' + this.risk!.payload.comment : ''}`;
      pdf.setFontSize(8);
      pdf.text(text, textLeft, y, { maxWidth: 80 });

      pdf.save(`${this.translate.instant('risks.risk_report')}-${this.risk!.id}-${format(new Date(), 'yyyy-MM-dd')}.pdf`);

    } catch (error) {
      console.error('Error generating PDF:', error);
      this.pop.error('Error generating PDF');
    } finally {
      this.loadingPDF = false;
    }
  }

  async getAttachment(attachmentId: string) {
    return new Promise(res => {
      let sub = this.attachmentService.getAttachment(attachmentId).subscribe(att => {
        sub.unsubscribe();
        res(att);
      });
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async toggleMap(event: MatSlideToggleChange) {
    this.showMap = event.checked;

    if (!this.showMap) {
      // Clean up marker when map is hidden
      let riskId: string = this.risk?.id || "";
      if (this.markers[riskId]) {
        this.markers[riskId].setMap(null);
      }

      // Clean up ground overlay
      if (this.groundOverlay) {
        this.groundOverlay.setMap(null);
      }
    } else if (this.risk?.payload?.lat && this.risk?.payload?.lon) {
      // Wait for the map to be rendered in the DOM
      await new Promise(res => setTimeout(res, 100));

      // Reinitialize the map and marker
      if (this.mapObject && Object.keys(this.mapObject).length > 0) {
        this.loadMap(this.mapObject);
        await this.setMarker();
      }
    }
  }
}
